import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal/Modal';
import promoUtils from 'utils/Promos';
const { CTA_TYPES, applyPromo, removePromo } = promoUtils;
import {
    Box, Text, Flex, Divider, Button, Link, Icon
} from 'components/ui';
import {
    fontSizes, fontWeights, buttons, space, colors, shadows, radii
} from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import rougeExclusiveUtils from 'utils/rougeExclusive';
import RougeExclusiveBadge from 'components/Badges/RougeExclusiveBadge';
import BICashModalEN from './locales/BiCashMultiplePromosModal_en_US';
import BICashModalFR from './locales/BiCashMultiplePromosModal_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BICashModalEN, BICashModalFR);

/**
 * Drawer modal for selecting and applying a Beauty Insider Cash promotion when multiple options are available.
 * Shows available CBR promotions and displays the current order subtotal.
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onDismiss - Callback to close the modal
 * @param {Object} [cbr] - CBR data from parent: { promos, subtotal, rawSubTotal, biPoints, error }
 */
const BiCashMultiplePromosModal = ({
    isOpen, onDismiss, cbr
}) => {

    const subtotal = cbr?.subtotal;
    const rawSubTotal = cbr?.rawSubTotal;
    const availableCBRPromotions = cbr?.promos;

    const isOneApplied = availableCBRPromotions?.some(promo => promo.isApplied);

    return (
        <Modal
            width={0}
            isOpen={isOpen}
            onDismiss={onDismiss}
            isDrawer={true}
            customStyle={styles.container}
        >
            <Modal.Header>
                <Modal.Title>
                    <Box css={styles.title}>
                        {getText('modalTitle')}
                    </Box>
                    <Box css={styles.modalSubTitle}
                        dangerouslySetInnerHTML={{
                            __html: getText('modalSubTitle', [cbr?.biPoints || 0])
                        }}
                    />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                paddingTop={0}
                paddingBottom={0}
                paddingX={4}
            >
                <Box
                    color={colors.gray}
                    paddingTop={3}
                    paddingBottom={2}
                >
                    {getText('chooseText')}
                </Box>
                {availableCBRPromotions?.map(
                    (
                        {
                            points,
                            localizedDiscountAmount,
                            couponCode,
                            promotionType,
                            isApplied,
                            isEligible,
                            isSegmentsExclusive = false,
                            segments = []
                        },
                        i
                    ) => {
                        const isCBRRougeExclusive = isSegmentsExclusive && segments.includes('ROUGE');

                        return (
                            <React.Fragment key={couponCode}>
                                <Flex
                                    flexDirection={'row'}
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                    paddingBottom={3}
                                >
                                    <Box>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: getText('biCashAmount', [localizedDiscountAmount])
                                            }}
                                        />
                                        <Flex
                                            alignItems={'flex-start'}
                                            gap={1}
                                        >
                                            <Box
                                                fontSize={fontSizes.sm}
                                                dangerouslySetInnerHTML={{
                                                    __html: getText('pointsAmount', [points])
                                                }}
                                            />
                                            {rougeExclusiveUtils.isRougeCashFlagEnabled && isCBRRougeExclusive && <RougeExclusiveBadge />}
                                        </Flex>
                                    </Box>
                                    {isApplied ? (
                                        <span>
                                            <Icon
                                                name={'checkmark'}
                                                size={'1em'}
                                                css={styles.checkmarkIcon}
                                            />
                                            <Text>{getText('applied')}</Text>
                                            <Link
                                                is={'p'}
                                                css={styles.removeLink}
                                                onClick={() => removePromo(couponCode.toLowerCase(), CTA_TYPES[promotionType])}
                                                children={getText('remove')}
                                            />
                                        </span>
                                    ) : (
                                        <Button
                                            variant={'secondary'}
                                            css={styles.applyButton}
                                            onClick={() => applyPromo(couponCode.toLowerCase(), null, CTA_TYPES[promotionType], true)}
                                            disabled={!isEligible || (isOneApplied && !isApplied)}
                                        >
                                            {getText('apply')}
                                        </Button>
                                    )}
                                </Flex>
                                {cbr?.error?.isAvailable && cbr?.error?.couponCode === couponCode.toUpperCase() && (
                                    <Box css={styles.error}>
                                        {cbr?.error?.message}
                                    </Box>
                                )}
                                {i !== availableCBRPromotions.length - 1 && (
                                    <Divider
                                        marginBottom={3}
                                        marginX={-4}
                                    />
                                )}
                            </React.Fragment>
                        );
                    }
                )}
            </Modal.Body>
            <Box
                position={'relative'}
                borderColor={colors.lightGray}
                borderTopWidth={1}
            >
                <Flex
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    paddingY={3}
                    paddingX={4}
                >
                    <Box fontWeight={fontWeights.bold}>{getText('subtotal')}</Box>
                    <Box paddingRight={3}>
                        {subtotal !== rawSubTotal && (
                            <Text css={styles.strikethroughText} >
                                {rawSubTotal}
                            </Text>
                        )}
                        <Text fontWeight={fontWeights.bold}>{subtotal}</Text>
                    </Box>
                </Flex>
                <Flex
                    fontSize={fontSizes.sm}
                    color={colors.gray}
                    paddingBottom={3}
                    paddingX={4}
                >
                    {getText('pointsAppliedLegal')}
                </Flex>
                <Box
                    boxShadow={shadows.light}
                    paddingX={4}
                    paddingY={3}
                    borderRadius={`0 0 ${radii[3]}px ${radii[3]}px`}
                >
                    <Button
                        variant='primary'
                        block={true}
                        onClick={onDismiss}
                        children={getText('done')}
                    />
                </Box>
            </Box>
        </Modal>
    );
};

const styles = {
    container: {
        height: '464px'
    },
    title: {
        fontSize: fontSizes.md,
        marginBottom: space[1] - 1
    },
    modalSubTitle: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.normal
    },
    checkmarkIcon: {
        position: 'absolute',
        transform: `translateX(calc(-100% - ${space[1]}px))`
    },
    removeLink: {
        fontSize: fontSizes.xs,
        fontWeight: fontWeights.bold,
        display: 'block',
        color: colors.blue
    },
    applyButton: {
        fontSize: fontSizes.sm,
        minHeight: buttons.HEIGHT_SM
    },
    error: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        marginBottom: space[1] / 2
    },
    strikethroughText: {
        textDecoration: 'line-through',
        paddingRight: space[1],
        color: colors.gray
    }
};

export default wrapFunctionalComponent(BiCashMultiplePromosModal, 'BiCashMultiplePromosModal');
